import React, { useState } from "react";
import {
  FaIdCard,
  FaEdit,
  FaCheck,
  FaTimes,
  FaUpload,
  FaSpinner,
} from "react-icons/fa";
import API from "../config/config";
import axios from "axios";
import DocumentStatusEditor from "./DocumentStatusEditor";
import { DocumentImage } from "./DocumentImageViewer";

const PAN_DUPLICATE_MESSAGE = "pan number is already used";
const PLACEHOLDER_PAN_VALUES = new Set(["", "TBD", "0", "00"]);

const isDuplicatePanMessage = (message) => {
  const normalized = String(message || "").toLowerCase();
  return normalized === PAN_DUPLICATE_MESSAGE;
};

const shouldValidatePanNumber = (value) => {
  const key = String(value || "").trim().replace(/[\s-]/g, "").toUpperCase();
  return key && !PLACEHOLDER_PAN_VALUES.has(key);
};

const PANDetails = ({ pan, driverId, editable, editableDetails, editableStatus, currentUserRole, showIfEmpty = false, onUpdated }) => {
  const token = localStorage.getItem("adminToken");
  const detailsEditable = editableDetails !== undefined ? editableDetails : editable;
  const statusEditable = editableStatus !== undefined ? editableStatus : editable;

  // ✅ These must always run regardless of any conditions
  const [editField, setEditField] = useState("");
  const [uploadingSide, setUploadingSide] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [panNumberError, setPanNumberError] = useState("");
  const [formData, setFormData] = useState({
    holderName: pan?.holderName || "",
    panNumber: pan?.panNumber || "",
    status: pan?.status || "",
    statusReason: pan?.statusReason || "",
    panfront: pan?.panfront || "",
  });

  // ✅ Perform the conditional return *after* hooks are defined
  const noPANData =
    !pan || (!pan.panNumber && !pan.panfront && !pan.holderName);

  // Show component if showIfEmpty is true, even when empty
  if (!showIfEmpty && noPANData) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "panNumber") {
      setPanNumberError("");
    }
    setFormData((p) => ({ ...p, [name]: value }));
  };

  const isPanNumberAlreadyUsed = async (trimmedPanNumber) => {
    const response = await axios.get(API.ADMIN.DUPLICATE_PAN_CHECK, {
      params: {
        panNumber: trimmedPanNumber,
        excludeDriverId: driverId,
      },
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data?.exists === true;
  };

  const handleFileChange = async (e, side) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingSide(side);

    try {
      // Upload image using /api/v1/admindocuments/upload
      const formdata = new FormData();
      formdata.append("documentFor", "Driver");
      formdata.append("type", "panfront");
      formdata.append("file", file, file.name);
      formdata.append("ownerId", driverId);

      const response = await fetch(API.ADMIN.ADMINDOCUMENT_UPLOAD, {
        method: "POST",
        headers: {
          Authorization: "Bearer " + token,
          // Do NOT set Content-Type manually
        },
        body: formdata,
      });

      if (!response.ok) {
        throw new Error(`Failed to upload ${side}`);
      }

      const data = await response.json();
      const uploadedUrl = data.document?.documentUrl;

      if (!uploadedUrl) throw new Error("Upload URL not returned.");

      // Update local state with new URL
      setFormData((prev) => ({
        ...prev,
        [side]: uploadedUrl,
      }));

      const panId = pan?._id || pan?.id;

      if (!panId) {
        // No PAN record yet: create it via admin submit so the image is stored (like Aadhaar)
        await axios.post(
          `${API.ADMIN.SUBMIT_PAN_CARD}/${driverId}`,
          {
            holderName: formData.holderName?.trim() || "To be updated",
            panNumber: formData.panNumber?.trim() || "TBD",
            panfront: uploadedUrl,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
      } else {
        // Update existing PAN record - only send panfront
        await axios.patch(`${API.ADMIN.PAN_DETAILS}/${panId}`, { panfront: uploadedUrl }, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
      }

      if (onUpdated) onUpdated();
    } catch (err) {
      console.error("Upload error:", err);
      alert("Failed to upload Pancard image.");
    } finally {
      setUploadingSide(null);
    }
  };

  const handleSubmit = async () => {
    // Validate all required fields
    if (!formData.holderName || !formData.panNumber || !formData.panfront) {
      alert("Please fill all fields: Full Name, PAN Number, and upload PAN Front Image");
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        holderName: formData.holderName,
        panNumber: formData.panNumber,
        panfront: formData.panfront,
      };

      const response = await axios.post(
        `${API.ADMIN.SUBMIT_PAN_CARD}/${driverId}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data && response.data.success === false) {
        throw new Error(response.data.message || "Failed to submit PAN details");
      }

      alert("PAN details submitted successfully!");
      if (onUpdated) {
        onUpdated();
      }
    } catch (err) {
      console.error("Submit error:", err);
      const errorMessage = err.response?.data?.message || err.message || "Failed to submit PAN details";
      alert(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveField = async (field) => {
    if (field === "panNumber") {
      const trimmed = (formData.panNumber || "").trim();
      const currentTrimmed = (pan?.panNumber || "").trim();
      if (
        shouldValidatePanNumber(trimmed) &&
        trimmed.replace(/[\s-]/g, "").toUpperCase() !==
          currentTrimmed.replace(/[\s-]/g, "").toUpperCase()
      ) {
        try {
          const alreadyUsed = await isPanNumberAlreadyUsed(trimmed);
          if (alreadyUsed) {
            setPanNumberError(PAN_DUPLICATE_MESSAGE);
            return;
          }
        } catch (checkErr) {
          console.error("PAN duplicate check error:", checkErr);
          setPanNumberError("Failed to validate pan number");
          return;
        }
      }
      setPanNumberError("");
    }

    setIsSaving(true);
    try {
      // Only send the specific field that was changed
      const payload = {};

      if (field === "status") {
        payload.status = formData.status;
        if (formData.status === "Rejected" || formData.status === "Revisit") {
          payload.statusReason = formData.statusReason;
        }

      } else if (field === "holderName") {
        // Only send holderName
        payload.holderName = formData.holderName;
      } else if (field === "panNumber") {
        // Only send panNumber
        payload.panNumber = formData.panNumber;
      } else {
        payload[field] = formData[field];
      }

      // Always use /api/admin/drivers/pan-verification/{id} for PAN info changes
      // Get the PAN ID - check both pan._id and pan.id
      const panId = pan?._id || pan?.id;

      if (panId) {
        // Update existing PAN record using PATCH - only send the changed field
        await axios.patch(`${API.ADMIN.PAN_DETAILS}/${panId}`, payload, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
      } else {
        const createBody =
          field === "panNumber"
            ? {
                holderName: formData.holderName?.trim() || "To be updated",
                panNumber: (formData.panNumber || "").trim(),
                panfront: formData.panfront?.trim() || "",
              }
            : {
                holderName: formData.holderName?.trim() || "To be updated",
                panNumber: formData.panNumber?.trim() || "TBD",
                panfront: formData.panfront?.trim() || "",
              };

        const createRes = await axios.post(
          `${API.ADMIN.SUBMIT_PAN_CARD}/${driverId}`,
          createBody,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (createRes.data?.success === false) {
          const apiMessage = createRes.data?.message || createRes.data?.error;
          if (field === "panNumber") {
            if (isDuplicatePanMessage(apiMessage)) {
              setPanNumberError(PAN_DUPLICATE_MESSAGE);
            } else {
              setPanNumberError(apiMessage || "Failed to update field");
            }
            return;
          }
          throw new Error(apiMessage || "Failed to update field");
        }

        const newPan = createRes.data?.data;
        const newPanId = newPan?._id || newPan?.id;

        if (newPanId && Object.keys(payload).length > 0 && field !== "panNumber") {
          await axios.patch(
            `${API.ADMIN.PAN_DETAILS}/${newPanId}`,
            payload,
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          );
        }
      }
      
      setEditField("");
      if (field === "panNumber") {
        setPanNumberError("");
      }
      if (onUpdated) {
        onUpdated();
      }
    } catch (err) {
      console.error("Update error:", err);
      console.error("PAN object:", pan);
      const apiMessage =
        err.response?.data?.error ||
        err.response?.data?.message ||
        err.message;
      if (field === "panNumber") {
        if (isDuplicatePanMessage(apiMessage)) {
          setPanNumberError(PAN_DUPLICATE_MESSAGE);
        } else {
          setPanNumberError(apiMessage || "Failed to update field");
        }
      } else {
        alert("Failed to update field");
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <section className="p-6">
      <div className="flex items-center gap-4 mb-6">
        <FaIdCard className="text-yellow-600 text-2xl" />
        <h2 className="text-2xl font-semibold text-gray-800">PAN Details</h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Full Name */}
        <div>
          <strong>Full Name:</strong>{" "}
          {editField === "holderName" ? (
            <span className="flex gap-2 items-center">
              <input
                name="holderName"
                value={formData.holderName}
                onChange={handleChange}
                className="border px-2 py-1 rounded text-sm"
              />
              <FaCheck
                className="text-green-600 cursor-pointer"
                onClick={() => handleSaveField("holderName")}
              />
              <FaTimes
                className="text-red-600 cursor-pointer"
                onClick={() => {
                  setFormData((p) => ({ ...p, holderName: pan?.holderName || "" }));
                  setEditField("");
                }}
              />
            </span>
          ) : (
            <span className="flex items-center gap-2">
              {formData.holderName || (showIfEmpty && detailsEditable ? "Click to add" : "N/A")}
              {detailsEditable && (
                <FaEdit
                  className="text-blue-600 cursor-pointer"
                  onClick={() => setEditField("holderName")}
                />
              )}
            </span>
          )}
        </div>

        {/* PAN Number */}
        <div>
          <strong>PAN Number:</strong>{" "}
          {editField === "panNumber" ? (
            <span className="inline-flex flex-col">
              <span className="flex gap-2 items-center">
                <input
                  name="panNumber"
                  value={formData.panNumber}
                  onChange={handleChange}
                  className="border px-2 py-1 rounded text-sm"
                />
                {isSaving ? (
                  <FaSpinner className="animate-spin text-gray-500" />
                ) : (
                  <>
                    <FaCheck
                      className="text-green-600 cursor-pointer"
                      onClick={() => handleSaveField("panNumber")}
                    />
                    <FaTimes
                      className="text-red-600 cursor-pointer"
                      onClick={() => {
                        setFormData((p) => ({ ...p, panNumber: pan?.panNumber || "" }));
                        setPanNumberError("");
                        setEditField("");
                      }}
                    />
                  </>
                )}
              </span>
              {panNumberError && (
                <p className="text-red-500 text-sm mt-1">{panNumberError}</p>
              )}
            </span>
          ) : (
            <span className="flex items-center gap-2">
              {formData.panNumber || (showIfEmpty && detailsEditable ? "Click to add" : "N/A")}
              {detailsEditable && (
                <FaEdit
                  className="text-blue-600 cursor-pointer"
                  onClick={() => {
                    setPanNumberError("");
                    setEditField("panNumber");
                  }}
                />
              )}
            </span>
          )}
        </div>

        {/* Images */}
        <div className="md:col-span-2">
          <strong className="block mb-2">PAN Card Images:</strong>
          <div className="flex gap-8 flex-wrap">
            {["panfront"].map((side) => (
              <div key={side} className="flex flex-col items-start">
                <span className="font-medium mb-1 capitalize">
                  {side === "panfront" ? "Front" : "Back"}
                </span>
                <DocumentImage
                  imageUrl={formData[side]}
                  imageName={`PAN Card ${side === "panfront" ? "Front" : "Back"
                    }`}
                  alt={`PAN ${side}`}
                >
                  {detailsEditable && (
                    <label className="mt-2 cursor-pointer flex gap-2 items-center text-sm text-blue-600">
                      {uploadingSide === side ? (
                        <FaSpinner className="animate-spin" />
                      ) : (
                        <>
                          <FaUpload />
                          {formData[side] ? "Change" : "Upload"}
                        </>
                      )}
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => handleFileChange(e, side)}
                        disabled={uploadingSide}
                      />
                    </label>
                  )}
                </DocumentImage>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Status Edit - Always show (same as AadhaarDetails) */}
      <DocumentStatusEditor
        statusForm={formData}
        setStatusForm={setFormData}
        isEditing={editField === "status"}
        onEdit={() => setEditField("status")}
        onCancel={() => {
          setFormData((prev) => ({
            ...prev,
            status: pan?.status,
            statusReason: pan?.statusReason,
          }));
          setEditField("");
        }}
        onSave={() => handleSaveField("status")}
        editable={statusEditable}
        userRole={currentUserRole}
      />
    </section>
  );
};

export default PANDetails;
